/**
 * Guest Join Screen - Shared guest join flow via share link
 */

import { useEffect, useState } from "react";
import { buildGamePath, buildGameRoomPath, navigateTo } from "../../router";
import { Alert, Button, Card, Input } from "../../ui";
import {
  getOrCreateGuestId,
  getSavedGuestNickname,
} from "../auth/guest-identity";
import {
  GUEST_NICKNAME_MAX_LENGTH,
  generateGuestNickname,
  getGuestNicknameLength,
  normalizeGuestNickname,
  validateGuestNickname,
} from "../auth/guest-nickname";
import { useAuthActions } from "../store/auth-store";
import { useLobbyActions } from "../store/lobby-store";

interface GuestJoinScreenProps {
  readonly gameType: string;
  readonly roomNumber: string;
  readonly secret: string;
  readonly seat?: "player" | "observer";
}

function getInitialGuestNickname(): string {
  const savedNickname = getSavedGuestNickname();
  if (savedNickname && validateGuestNickname(savedNickname) === null) {
    return savedNickname;
  }
  return generateGuestNickname();
}

export function GuestJoinScreen({
  gameType,
  roomNumber,
  secret,
  seat = "player",
}: GuestJoinScreenProps) {
  const authActions = useAuthActions();
  const lobbyActions = useLobbyActions();

  const [username, setUsername] = useState(getInitialGuestNickname);
  const [isValidating, setIsValidating] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedGameType, setResolvedGameType] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!gameType || !roomNumber || !secret) {
        setError("공유 링크가 올바르지 않습니다.");
        setIsValidating(false);
        return;
      }

      lobbyActions.setGameType(gameType);

      const resolved = await lobbyActions.resolveShareLink(
        gameType,
        roomNumber,
        secret,
      );
      if (!resolved) {
        setError("공유 링크가 만료되었거나 유효하지 않습니다.");
        setIsValidating(false);
        return;
      }

      setResolvedGameType(gameType);
      setIsValidating(false);
    };

    void run();
  }, [gameType, roomNumber, secret, lobbyActions]);

  const handleJoin = async () => {
    const validationError = validateGuestNickname(username);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsJoining(true);
    setError(null);

    const normalizedUsername = normalizeGuestNickname(username);
    const guestId = getOrCreateGuestId();
    const loginSuccess = await authActions.loginAsGuestWithId(
      guestId,
      normalizedUsername,
    );
    if (!loginSuccess) {
      setError("게스트 로그인에 실패했습니다. 다시 시도해주세요.");
      setIsJoining(false);
      return;
    }

    const room =
      seat === "observer"
        ? await lobbyActions.joinObserverByShareLink(
            gameType,
            roomNumber,
            secret,
          )
        : await lobbyActions.joinByShareLink(gameType, roomNumber, secret);
    if (!room) {
      setError("방 참가에 실패했습니다. 링크 상태를 확인해주세요.");
      setIsJoining(false);
      return;
    }

    navigateTo(
      seat === "observer"
        ? buildGamePath(gameType, room.id)
        : buildGameRoomPath(gameType, room.id),
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 sm:p-6">
      <Card className="w-full shadow-lg sm:max-w-md sm:p-8">
        <h1 className="mb-2 text-center font-semibold text-2xl">게스트 참여</h1>
        <p className="mb-6 text-center text-sm text-text-secondary">
          공유 링크로 초대받은 게임에 참여합니다
        </p>

        {isValidating && (
          <Alert variant="info" className="mb-4 text-center">
            공유 링크 확인 중...
          </Alert>
        )}

        {!isValidating && resolvedGameType && (
          <Alert variant="info" className="mb-4">
            🔗 {resolvedGameType} — 방 #{roomNumber}
            {seat === "observer" ? " — 관전" : ""}
          </Alert>
        )}

        {error && <Alert className="mb-4">{error}</Alert>}

        {!isValidating && resolvedGameType && (
          <div className="flex flex-col gap-3">
            <div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  data-testid="guest-username-input"
                  placeholder="게스트 닉네임"
                  value={username}
                  maxLength={GUEST_NICKNAME_MAX_LENGTH}
                  onChange={(event) => setUsername(event.target.value)}
                  disabled={isJoining}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  data-testid="guest-random-name-btn"
                  onClick={() => setUsername(generateGuestNickname())}
                  disabled={isJoining}
                  className="shrink-0"
                >
                  랜덤
                </Button>
              </div>
              <div className="mt-1 flex justify-between gap-2 text-text-muted text-xs">
                <span>한글 자모, 영문, 숫자, -, _만 가능</span>
                <span data-testid="guest-username-count">
                  {getGuestNicknameLength(username)} /{" "}
                  {GUEST_NICKNAME_MAX_LENGTH}
                </span>
              </div>
            </div>
            <Button
              variant="success"
              data-testid="join-guest-btn"
              onClick={() => void handleJoin()}
              disabled={isJoining}
            >
              {isJoining
                ? "참여 중..."
                : seat === "observer"
                  ? "관전 입장"
                  : "게스트 참여"}
            </Button>
          </div>
        )}

        <p className="mt-6 text-center text-sm text-text-secondary">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateTo("/login")}
            className="px-0 py-0"
          >
            계정으로 로그인
          </Button>
        </p>
      </Card>
    </div>
  );
}
